const user = state => state.user

const userIsLoggedIn = state => {
  if (state.currentJWT && state.user) {
    return true
  }

  return false
}

const userIsActivated = state => {
  if (state.user && state.user.activated) {
    return true
  }

  return false
}

const userHasNoNursery = state => {
  if (state.user && state.user.nurseries.data.length === 0) {
    return true
  }

  return false
}

const userHasNoBranch = state => {
  if (!state.user || state.user.nurseries.data.length === 0 || !state.user.nurseries.data[0].branches) {
    return true
  }

  return false
}

const userHasNoStaffCategory = state => {
  if (!state.user || state.user.nurseries.data.length === 0 || !state.user.nurseries.data[0].branches.data.find(branch => branch.staff_category)) {
    return true
  }

  return false
}

const userHasCompletedMigration = state => {
  if (state.activeNursery && state.activeNurseryBranch && state.activeNursery.provider_type && state.activeNurseryBranch.service_types.data.length != 0 && state.activeNurseryBranch.hourly_rate && state.activeNurseryBranch.payment_types.data.length != 0 && state.activeNurseryBranch.local_authority_id) {

    state.userHasCompletedMigration = true
  }
  return state.userHasCompletedMigration
}

const userHasViewedIntro = state => {
  if (state.user && state.user.intro_viewed) {
    return true
  }

  return false
}

const activeNurseryBranchSetupIsComplete = state => {
  if ((state.role === 'nursery-super-admin' || state.role === 'nursery-admin') && state.activeNurseryBranch && state.activeNurseryBranch.branch_details_complete && state.activeNurseryBranch.planning_complete && state.activeNurseryBranch.accounts_complete /*&& state.activeNurseryBranch.staff_complete*/) {
    return true
  } else if (state.role === 'nursery-staff' && state.user.activity_per_branch[state.activeNurseryBranch.id]) {
    return true
  }

  return false
}

const userHasChildren = state => {
  if (state.user && state.user.keyworkerProfile && state.user.keyworkerProfile.children.data.length > 0) {
    return true
  } else {
    return false
  }
}

const userChildren = (state, getters) => getters.userHasChildren ? state.user.keyworkerProfile.children.data : []

const observerChildren = (state, getters) => {
  return state.user.allCarerProfiles?.data.map((profile) => {
    return profile.children.data
  }).flat()
}

const getUserChildById = (state, getters) => id => {
  return getters.userChildren.find( child => child.id == id)
}

// True while a provider has registered a child against this keyworker's email
// (an observer child) that they haven't yet resolved into one of their own
// children. The backend computes this per-observer (has_unresolved_observer_children),
// so it stays true through the PARTIAL state — claimed one child but left
// another, or a duplicate merge, unfinished. That's the case the old
// "observers > 0 && subjects === 0" trigger fell through, because claiming one
// child never shrinks the observer list.
const hasUnresolvedObserverChildren = (state, getters) => {
  if (!state.user) return false
  if (getters.role !== 'keyworker') return false

  const flag = state.user.has_unresolved_observer_children
  if (typeof flag === 'boolean') return flag

  // Fallback for API payloads predating the field: best-effort from the
  // client-visible counts. Misses the partial case, but never worse than before.
  const observers = getters.observerChildren || []
  const subjects = getters.userChildren || []
  return observers.length > 0 && subjects.length === 0
}

// Forced wizard redirect (router guard). Only the truly-fresh parent — no
// children of their own yet — is interrupted. A parent who has already claimed
// at least one child is guided via the passive recovery entry point instead of
// being trapped in the wizard. Suppressed once they've explicitly said "none of
// these are mine" (persisted per-user-id client-side).
const needsToConfirmChildren = (state, getters) => {
  if (!getters.hasUnresolvedObserverChildren) return false
  // Defensive `|| []` — persisted Vuex state from before this release won't
  // have this key; if localStorage ever hydrates it as undefined/null (has
  // happened with persisted-state upgrades) .includes() would throw and the
  // router guards would break for every logged-in keyworker.
  if ((state.dismissedConfirmChildrenPromptUserIds || []).includes(state.user.id)) return false
  const subjects = getters.userChildren || []
  return subjects.length === 0
}

// Passive recovery entry point (account-menu link, home banner). Available
// whenever an observer child is still unresolved — deliberately NOT gated by
// the "not mine" dismissal, so an accidental skip or a half-finished merge is
// never a dead-end. The menu link is the always-on safety net; the banner
// additionally respects the dismissal so it doesn't pester a genuine "not mine".
const canRecoverChildren = (state, getters) => getters.hasUnresolvedObserverChildren

// True while the complete-profile step has never been run. That step is what
// copies the provider-entered details into the parent's own profile; entering
// the confirm-children wizard before it means accepting a child syncs an
// empty profile over the provider's carer record and blanks it (staging
// incident, 2026-07-01). Two signals, both must say "never ran":
//  - a per-user marker persisted when the step submits (survives the fact
//    that the form only enforces first/last name, so a completed profile can
//    legitimately hold no contact details), and
//  - the contact fields themselves. Names are no signal at all: the invite
//    copies them onto the user before the parent ever logs in.
// Worst case on a fresh device (no marker, no contact details) is one extra
// pass through the prefilled profile page, never a loop: submitting sets the
// marker before the router moves on.
const needsToCompleteProfile = (state) => {
  if (!state.user) return false
  if ((state.completedProfileStepUserIds || []).includes(state.user.id)) return false
  return !state.user.mobile_number && !state.user.telephone_number &&
    !state.user.address_line_1 && !state.user.postcode
}

const confirmChildrenDismissed = (state) => {
  if (!state.user) return false
  return (state.dismissedConfirmChildrenPromptUserIds || []).includes(state.user.id)
}

const userIsKeyWorker = state => {
  if (state.user && state.role === 'keyworker') {
    return true
  }

  return false
}

const userIsStaff = state => {
  return state.activeNursery && state.activeNursery.staffProfile
}

const userIsSessionalStaff = state => {
  return userIsStaff && state.activeNursery.staffProfile.employment_type == 2
}

const userIsFullTimeStaff = state => {
  return userIsStaff && state.activeNursery.staffProfile.employment_type == 0
}

const userIsPartTimeStaff = state => {
  return userIsStaff && state.activeNursery.staffProfile.employment_type == 1
}

const jumpStepBack = state => state.jumpStepBack

const jwt = state => state.currentJWT
const jwtData = (state, getters) => state.currentJWT ? JSON.parse(atob(getters.jwt.split('.')[1])) : null
const jwtSubject = (state, getters) => getters.jwtData ? getters.jwtData.sub : null
const jwtIssuer = (state, getters) => getters.jwtData ? getters.jwtData.iss : null

const authIsReady = state => state.authIsReady

const activeNursery = state => state.activeNursery
const activeNurseryBranch = state => state.activeNurseryBranch
const hideActiveNurseryBranchSetupFooter = state => state.hideActiveNurseryBranchSetupFooter
const openingTimes = state => state.openingTimes
const role = state => state.user? (state.activeNursery && state.activeNursery.staffProfile && state.activeNursery.staffProfile.role? state.activeNursery.staffProfile.role.slug: (state.user.role && state.user.role.slug ? state.user.role.slug : null)) : null

const userIsNurseryAdmin = state => {
  if (state.user && state.role === 'nursery-super-admin') {
    return true
  }
  return false
}

const userIsNurseryStaff = state => {
  if (state.user && state.role === 'nursery-staff') {
    return true
  }
  return false
}

const userHasStartMonth = state => {
  if(state.activeNursery && state.activeNursery.start_month && state.activeNursery.start_month !== '0') {
    return true
  }
  return false
}

const childNumber = state => state.childNumber

const triggerDashboardModal = state => {

  if (state.activeNursery && state.activeNurseryBranch && (!state.activeNursery.provider_type || !state.activeNurseryBranch.service_types || !state.activeNurseryBranch.hourly_rate || !state.activeNurseryBranch.payment_types || !state.activeNurseryBranch.local_authority_id)) {
    return true
  } else {
    return false
  }
}
const checkNotifications = state => state.checkNotifications

const updateChildren = state => state.updateChildren
const userHasEnabledMarketplace = state => state.userHasEnabledMarketplace
const upgraded = state => state.upgraded
const userHasSuspendedNursery = state => state.userHasSuspendedNursery
const userHasUpdatedChildren = state => state.userHasUpdatedChildren

const userActiveNurseryEmploymentType = state => {
  if (state.activeNursery && state.activeNursery.staffProfile) {
    return state.activeNursery.staffProfile.employment_type
  }
  return null
}

const redirectFromSignup = state => state.redirectFromSignup

const isServiceOnboarding = (state, getters) => {
  return getters.isServiceOnboardingStepOne || getters.isServiceOnboardingStepTwo
}

const isServiceOnboardingStepOne = (state, getters) => {
  return getters.userIsLoggedIn && getters.role == 'nursery-super-admin' && getters.userHasNoNursery && getters.userHasNoBranch
}

const isServiceOnboardingStepTwo = (state, getters) => {
  return getters.userIsLoggedIn && getters.role == 'nursery-super-admin' && !getters.userHasNoNursery && !getters.userHasNoBranch && !getters.isActiveNurseryBranchDetailsComplete  
}

const isActiveNurseryBranchDetailsComplete = state => {
  return state.activeNurseryBranch && state.activeNurseryBranch.branch_details_complete
}

const getServiceById = state => id => {
  return state.activeNursery.branches.data.find( service => service.id == id)
}

const userHasBasicInfo = state => state.user.first_name && state.user.last_name && state.user.telephone_number

const userHasAddress = state => (state.user.address_line_1 && state.user.postcode) ? true : false

const userPaymentMethods = state => {
  if (state.user.keyworkerProfile && state.user.keyworkerProfile.payment_methods.data.length > 0) {
    return state.user.keyworkerProfile.payment_methods.data
  } else {
    return []
  }
}

const userHasNoPaymentMethod = ( state, getters ) => getters.userPaymentMethods.length ? false : true

const serviceHasStripeConnectedAccount = ( state, getters ) => state.activeNurseryBranch.has_stripe_connected_account

const userHasKeyworkerRole = state => state.user && state.user.roles.data.find( i => i.slug == 'keyworker') ? true : false

const userHasNurseryRole = state => state.user && state.user.roles.data.find( i => i.slug == 'nursery-super-admin'
    || i.slug == 'nursery-admin'
    || i.slug == 'nursery-staff'
  ) ? true : false

const serviceAcceptedPaymentMethods = state => state.activeNurseryBranch ? state.activeNurseryBranch.payment_types.data : []

const userIsEligibleForEnquiry = ( state, getters ) => getters.userIsLoggedIn && getters.userHasAddress && getters.userHasChildren

export default {
  user,
  userHasViewedIntro,
  userIsLoggedIn,
  userIsActivated,
  userHasNoNursery,
  userHasNoBranch,
  userHasNoStaffCategory,
  userIsNurseryAdmin,
  userIsNurseryStaff,
  userHasStartMonth,
  childNumber,
  jwt,
  jwtData,
  jwtSubject,
  jwtIssuer,
  authIsReady,
  activeNursery,
  activeNurseryBranch,
  activeNurseryBranchSetupIsComplete,
  hideActiveNurseryBranchSetupFooter,
  openingTimes,
  role,
  triggerDashboardModal,
  userHasChildren,
  userChildren,
  observerChildren,
  getUserChildById,
  hasUnresolvedObserverChildren,
  needsToConfirmChildren,
  canRecoverChildren,
  needsToCompleteProfile,
  confirmChildrenDismissed,
  getServiceById,
  userIsKeyWorker,
  jumpStepBack,
  userHasCompletedMigration,
  checkNotifications,
  updateChildren,
  userHasEnabledMarketplace,
  upgraded,
  userHasSuspendedNursery,
  userHasUpdatedChildren,
  userIsStaff,
  userIsSessionalStaff,
  userIsFullTimeStaff,
  userIsPartTimeStaff,
  userActiveNurseryEmploymentType,
  redirectFromSignup,
  isServiceOnboarding,
  isServiceOnboardingStepOne,
  isServiceOnboardingStepTwo,
  isActiveNurseryBranchDetailsComplete,
  userHasBasicInfo,
  userHasAddress,
  userPaymentMethods,
  userHasNoPaymentMethod,
  serviceHasStripeConnectedAccount,
  userHasKeyworkerRole,
  userHasNurseryRole,
  serviceAcceptedPaymentMethods,
  userIsEligibleForEnquiry,
}
